import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FiCalendar, FiClock, FiCheckCircle, FiXCircle, 
  FiSend, FiFileText, FiList, FiPieChart, FiTrendingUp,
  FiCornerUpRight, FiMinusCircle, FiAlertTriangle, FiInfo,
  FiPlusCircle, FiAward, FiAlertCircle,
  FiChevronDown, FiChevronUp, FiLayers, FiArrowRight, FiArrowLeft
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import ConfirmModal from '../shared/ConfirmModal';
import { fmtDate, getCurrentFinancialYear } from '../../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TOTAL_ANNUAL_DAYS = 24;
const LEAVE_TYPES = ['ANNUAL', 'SICK', 'EMERGENCY'];

const STATUS_UI = {
  APPROVED: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: FiCheckCircle },
  PENDING: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: FiClock },
  REJECTED: { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', icon: FiXCircle },
  CANCELLED: { color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', icon: FiXCircle },
};

const getTomorrowStrInit = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const computeEndDate = (startStr, duration) => {
  if (!startStr || !duration || duration < 1) return startStr || '';
  const d = new Date(startStr + 'T00:00:00'); // parse as local
  const [y, m, day] = startStr.split('-').map(Number);
  const start = new Date(y, m - 1, day);
  start.setDate(start.getDate() + Number(duration) - 1);
  return `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}`;
};

const INITIAL_FORM = { 
  leaveType: 'ANNUAL', 
  startDate: getTomorrowStrInit(), 
  endDate: getTomorrowStrInit(), 
  duration: 1, 
  reason: '',
  isHalfDay: false,
  halfDaySession: 'FIRST_HALF'
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const canCancelLeave = (leaveStartDate) => {
  if (!leaveStartDate) return false;
  const now = new Date();
  
  // Format local date as YYYY-MM-DD
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  
  if (todayStr > leaveStartDate) return false;
  if (todayStr === leaveStartDate && now.getHours() >= 22) return false;
  
  return true;
};


export default function EmpLeaveManagement({ 
  activeTab = 'balance', 
  setActiveTab,
  selectedYear: propSelectedYear,
  setSelectedYear: propSetSelectedYear
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [internalYear, setInternalYear] = useState(getCurrentFinancialYear());
  const selectedYear = propSelectedYear !== undefined ? propSelectedYear : internalYear;
  const setSelectedYear = propSetSelectedYear || setInternalYear;
  const [selectedSaturdayMonth, setSelectedSaturdayMonth] = useState(new Date().getMonth() + 1);
  const [reasonTouched, setReasonTouched] = useState(false);

  // Preview state for Apply Leave tab
  const [preview, setPreview] = useState(null);  // { daysCount, paidDays, unpaidDays } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewDebounce = useRef(null);
  const endDatePickerRef = useRef(null);

  const [leaveBalance, setLeaveBalance] = useState({
    annualLimit: TOTAL_ANNUAL_DAYS,
    totalTakenYear: 0,
    paidTakenYear: 0,
    totalTakenMonth: 0,
    paidTakenMonth: 0,
    currentMonthAccrual: 2,
    currentYearAccrued: 0,
    cumulativeAvailable: TOTAL_ANNUAL_DAYS,
    totalCarryForward: 0,
    carryForward: 0,
    carriedForward: 0,
    earnedLeave: 0,
    encashedDays: 0,
    financialYear: null,
    financialYearLabel: '',
    totalPaidLeavesAvailableYear: TOTAL_ANNUAL_DAYS,
    totalPaidLeavesAvailableMonth: 0,
    monthlyPaidLeaveLimit: 3,
    currentAvailableBalance: TOTAL_ANNUAL_DAYS,
    lopLeavesMonth: 0,
    lopLeavesYear: 0,
    lopDeductionAmount: null,
    lopDeductionNote: null,
    pendingRequests: [],
    approvedRequests: [],
    rejectedRequests: []
  });

  // Carry forward leaves does not include current month leaves (carry forward = carry forward - 2, never < 0)
  const computedCarryForward = useMemo(() => {
    const raw =
      leaveBalance.cumulativeAvailable ??
      leaveBalance.accumulatedPriorSurplus ??
      leaveBalance.totalCarryForward ??
      leaveBalance.carryForward ??
      Math.max(
        0,
        (Number(leaveBalance.currentYearAccrued ?? 0) +
          Number(leaveBalance.carriedForward ?? leaveBalance.totalCarryForward ?? 0) +
          Number(leaveBalance.earnedLeave ?? 0)) -
          Number(leaveBalance.paidTakenYear ?? 0)
      );
    return Math.max(0, Number(raw || 0) - 2);
  }, [leaveBalance]);

  const [saturdayData, setSaturdayData] = useState({
    totalLopDays: 0,
    saturdaysWorked: 0,
    pendingSaturdaysToWork: 0
  });

  const [holidays, setHolidays] = useState([]);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingSaturday, setLoadingSaturday] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState({ open: false, warnings: [], unpaidDays: 0 });
  const [lopAccepted, setLopAccepted] = useState(false);
  const [requestFilter, setRequestFilter] = useState('ALL');

  const getCurrentMonthStr = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
  };

  const getMonthDateRange = (monthStr) => {
    if (!monthStr) return { start: '', end: '' };
    const [year, month] = monthStr.split('-').map(Number);
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { start: startDate, end: endDate };
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthStr());
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceDates, setAttendanceDates] = useState(() => getMonthDateRange(getCurrentMonthStr()));
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, leaveId: null, loading: false });

  const handleMonthChange = (e) => {
    const monthVal = e.target.value;
    setSelectedMonth(monthVal);
    if (monthVal) {
      setAttendanceDates(getMonthDateRange(monthVal));
    } else {
      setAttendanceDates({ start: '', end: '' });
    }
  };

  const fetchLeaveHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/leave/my-leaves');
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.content)
        ? raw.content
        : Array.isArray(raw?.data)
        ? raw.data
        : [];
      setLeaveHistory(list);
    } catch (err) {
      console.error('Failed to load leave history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchLeaveBalance = useCallback(async (yearToFetch = selectedYear) => {
    setLoadingBalance(true);
    try {
      const res = await api.get(`/leave/monthly-balance/${yearToFetch}`);
      const data = res.data?.data || res.data;
      if (data) {
        setLeaveBalance(data);
      }
    } catch (err) {
      console.error('Failed to load leave balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  }, [selectedYear]);

  const fetchSaturdayMakeup = useCallback(async (m = selectedSaturdayMonth, y = selectedYear) => {
    setLoadingSaturday(true);
    try {
      const res = await api.get(`/leave/saturday-makeup/${m}/${y}`);
      const data = res.data?.data || res.data;
      if (data) {
        setSaturdayData(data);
      }
    } catch (err) {
      console.error('Failed to load Saturday makeup summary:', err);
    } finally {
      setLoadingSaturday(false);
    }
  }, [selectedSaturdayMonth, selectedYear]);

  const holidaysFetchedRef = useRef(false);
  const previewAbortRef = useRef(null);

  const getTodayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getTomorrowStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const toLocalDateStr = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseLocalDate = (str) => {
    if (!str) return new Date();
    const parts = String(str).split('T')[0].split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return new Date(str);
  };

  const fetchHolidays = useCallback(async () => {
    if (holidaysFetchedRef.current) return;
    try {
      const res = await api.get('/holidays');
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setHolidays(list);
      holidaysFetchedRef.current = true;
    } catch (err) {
      console.error('Failed to load holidays:', err);
    }
  }, []);

  const fetchAttendanceHistory = useCallback(async () => {
    setLoadingAttendance(true);
    try {
      let url = '/attendance/my';
      const params = new URLSearchParams();
      if (attendanceDates.start) params.append('startDate', attendanceDates.start);
      if (attendanceDates.end) params.append('endDate', attendanceDates.end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      setAttendanceHistory(res.data || []);
    } catch (err) {
      toast.error('Failed to load attendance history.');
    } finally {
      setLoadingAttendance(false);
    }
  }, [attendanceDates]);

  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchLeaveHistory(), 
      fetchLeaveBalance(selectedYear), 
      fetchSaturdayMakeup(selectedSaturdayMonth, selectedYear),
      fetchHolidays(),
    ]);
  }, [fetchLeaveHistory, fetchLeaveBalance, selectedYear, fetchSaturdayMakeup, selectedSaturdayMonth, fetchHolidays]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendanceHistory();
    }
  }, [activeTab, attendanceDates.start, attendanceDates.end, fetchAttendanceHistory]);

  const getMinStartDate = (leaveType) => {
    if (leaveType === 'ANNUAL') {
      return getTomorrowStr();
    }
    return getTodayStr();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...form, [name]: value };

    if (name === 'isHalfDay') {
      const isHalf = Boolean(value);
      updated.isHalfDay = isHalf;
      if (isHalf) {
        updated.duration = 0.5;
        updated.endDate = updated.startDate;
        if (!updated.halfDaySession) {
          updated.halfDaySession = 'FIRST_HALF';
        }
      } else {
        updated.duration = 1;
        updated.endDate = computeEndDate(updated.startDate, 1);
      }
    }

    // If changing to ANNUAL, ensure startDate is at least tomorrow
    if (name === 'leaveType' && value === 'ANNUAL') {
      const todayStr = getTodayStr();
      if (updated.startDate && updated.startDate <= todayStr) {
        const tomorrowStr = getTomorrowStr();
        updated.startDate = tomorrowStr;
        updated.endDate = updated.isHalfDay ? tomorrowStr : computeEndDate(tomorrowStr, updated.duration);
        toast('Annual leave can be applied only before one day. Start date adjusted to tomorrow.', {
          icon: 'ℹ️',
          duration: 3500,
        });
      }
    }

    if (name === 'startDate') {
      const todayStr = getTodayStr();
      if (form.leaveType === 'ANNUAL' && value <= todayStr) {
        toast.error('Annual leave can be applied only before one day (cannot start today or in the past).');
        return;
      }
      // Recompute end date from new start + existing duration
      updated.endDate = updated.isHalfDay ? value : computeEndDate(value, updated.duration);
    }

    if (name === 'duration') {
      const parsed = parseFloat(value);
      if (parsed === 0.5 || (parsed > 0 && parsed < 1)) {
        updated.duration = 0.5;
        updated.isHalfDay = true;
        updated.endDate = updated.startDate;
        if (!updated.halfDaySession) {
          updated.halfDaySession = 'FIRST_HALF';
        }
      } else if (!isNaN(parsed) && parsed >= 1) {
        const dur = Math.max(1, Math.round(parsed));
        updated.duration = dur;
        updated.isHalfDay = false;
        updated.endDate = computeEndDate(updated.startDate, dur);
      }
    }

    setForm(updated);
    // Debounce preview fetch when dates are filled
    if (updated.startDate && updated.endDate && updated.startDate <= updated.endDate) {
      clearTimeout(previewDebounce.current);
      previewDebounce.current = setTimeout(() => fetchPreview(updated), 600);
    } else {
      setPreview(null);
      setPreviewError(null);
    }
  };

  const fetchPreview = async (f) => {
    if (!f.startDate || !f.endDate) return;
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }
    const abortController = new AbortController();
    previewAbortRef.current = abortController;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    try {
      const toDateArray = (s) => s.split('-').map(Number);
      const res = await api.post('/leave/preview', {
        leaveType: f.leaveType,
        startDate: toDateArray(f.startDate),
        endDate: toDateArray(f.endDate),
        reason: f.reason || '',
        isHalfDay: !!f.isHalfDay,
        halfDaySession: f.isHalfDay ? f.halfDaySession : null,
      }, { signal: abortController.signal });
      const d = res.data?.data ?? res.data;
      setPreview({
        daysCount: d.daysCount ?? 0,
        paidDays: d.paidDays ?? 0,
        unpaidDays: d.unpaidDays ?? 0,
      });
      setPreviewError(null);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setPreview(null);
        const data = err.response?.data;
        const errMsg = data?.message || data?.error || (typeof data === 'string' ? data : err.message) || '';
        const msgStr = typeof errMsg === 'string' ? errMsg : (errMsg?.message || '');
        if (msgStr.toLowerCase().includes('overlap') || msgStr.toLowerCase().includes('existing')) {
          setPreviewError('Leave dates overlap with an existing leave application');
        } else if (msgStr.trim()) {
          setPreviewError(msgStr);
        } else {
          setPreviewError('Leave dates overlap with an existing leave application');
        }
      }
    } finally {
      if (previewAbortRef.current === abortController) {
        setPreviewLoading(false);
      }
    }
  };

  const formatDateString = (dateObj) => {
    if (!dateObj) return '';
    if (Array.isArray(dateObj)) return dateObj.map(n => String(n).padStart(2, '0')).join('-');
    return dateObj.toString();
  };

  const validateAndSubmit = async (e) => {
    e.preventDefault();
    if (!form.reason || !form.reason.trim()) {
      setReasonTouched(true);
      return toast.error('Please provide a reason for your leave request.');
    }
    const todayStr = getTodayStr();
    if (form.leaveType === 'ANNUAL' && form.startDate <= todayStr) {
      return toast.error('Annual leave can be applied only before one day (cannot start today or in the past).');
    }
    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      return toast.error('Start date cannot be after end date.');
    }
    if (previewError && (previewError.toLowerCase().includes('overlap') || previewError.toLowerCase().includes('existing'))) {
      return toast.error('Leaves are overlapping with an existing leave application. Please choose different dates.');
    }

    if (form.isHalfDay) {
      if (form.startDate !== form.endDate) {
        return toast.error('Half-day leave must start and end on the same day.');
      }
      let availablePaid = leaveBalance.currentAvailableBalance || 0;
      let paidDays = Math.min(0.5, availablePaid);
      let unpaidDays = 0.5 - paidDays;

      if (preview && typeof preview.unpaidDays === 'number') {
        unpaidDays = preview.unpaidDays;
        paidDays = preview.paidDays ?? paidDays;
      }

      const warnings = [];
      if (unpaidDays > 0) {
        warnings.push(`This half-day request will result in Loss of Pay (LOP) for ${unpaidDays} day based on your available balance and monthly limit.`);
      }

      if (warnings.length > 0) {
        setPendingSubmit({ open: true, warnings, unpaidDays });
        setLopAccepted(false);
        setSubmitting(false);
      } else {
        executeSubmit();
      }
      return;
    }

    try {
      setSubmitting(true);
      
      const isNonWorkingDay = (date) => {
        const day = date.getDay();
        if (day === 0 || day === 6) return true;
        const dateStr = toLocalDateStr(date);
        return holidays.some(h => formatDateString(h.date) === dateStr);
      };

      let workingDaysCount = 0;
      let tempDate = parseLocalDate(form.startDate);
      const endDt = parseLocalDate(form.endDate);
      while (tempDate <= endDt) {
        if (!isNonWorkingDay(tempDate)) {
          workingDaysCount++;
        }
        tempDate.setDate(tempDate.getDate() + 1);
      }
      
      const getFormattedDayInfo = (dateObj) => {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[dateObj.getDay()];
        const d = String(dateObj.getDate()).padStart(2, '0');
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const y = dateObj.getFullYear();
        return `${d}-${m}-${y} (${dayName})`;
      };

      // Sandwich policy removed: effective leave duration is strictly the number of working days
      let effectiveDaysCount = workingDaysCount;

      let enclosedNonWorkingList = [];
      let calcTemp = parseLocalDate(form.startDate);
      while (calcTemp <= endDt) {
        if (isNonWorkingDay(calcTemp)) {
          enclosedNonWorkingList.push(getFormattedDayInfo(new Date(calcTemp)));
        }
        calcTemp.setDate(calcTemp.getDate() + 1);
      }

      const warnings = [];

      if (workingDaysCount === 0) {
        warnings.push(`Selected date range contains only weekends or non-working holidays. 0 working days will be deducted.`);
      } else if (enclosedNonWorkingList.length > 0) {
        const startInfo = getFormattedDayInfo(parseLocalDate(form.startDate));
        const endInfo = getFormattedDayInfo(parseLocalDate(form.endDate));
        const selectedRangeStr = form.startDate === form.endDate ? startInfo : `${startInfo} to ${endInfo}`;
        warnings.push(`This request covers ${selectedRangeStr}. Non-working days (${enclosedNonWorkingList.join(', ')}) are excluded. Actual leave charged: ${workingDaysCount} working day(s).`);
      }

      let availablePaid = leaveBalance.currentAvailableBalance || 0;
      let paidDays = Math.min(effectiveDaysCount, availablePaid);
      let unpaidDays = effectiveDaysCount - paidDays;

      // If we have an active server preview result for these dates, use its verified dynamic monthly cap and unpaid calculation
      if (preview && typeof preview.unpaidDays === 'number') {
        unpaidDays = preview.unpaidDays;
        paidDays = preview.paidDays ?? paidDays;
      }

      if (unpaidDays > 0) {
        warnings.push(`This request will result in Loss of Pay (LOP) for ${unpaidDays} day(s) based on your available balance and monthly limit.`);
      }

      if (warnings.length > 0) {
        setPendingSubmit({ open: true, warnings, unpaidDays });
        setLopAccepted(false);
        setSubmitting(false);
      } else {
        executeSubmit();
      }
    } catch (err) {
      toast.error('Error validating request');
      setSubmitting(false);
    }
  };

  const executeSubmit = async () => {
    const toDateArray = (dateStr) => dateStr.split('-').map(Number);
    const payload = {
      leaveType: form.leaveType,
      startDate: toDateArray(form.startDate),
      endDate: toDateArray(form.endDate),
      reason: form.reason,
      isHalfDay: !!form.isHalfDay,
      halfDaySession: form.isHalfDay ? form.halfDaySession : null,
    };

    try {
      setSubmitting(true);
      await api.post('/leave/apply', payload);
      toast.success('Leave request submitted successfully!');
      setForm({ ...INITIAL_FORM, startDate: getTomorrowStr(), endDate: getTomorrowStr(), duration: 1, isHalfDay: false, halfDaySession: 'FIRST_HALF' });
      setReasonTouched(false);
      setPendingSubmit({ open: false, warnings: [] });
      setPreview(null);
      setPreviewError(null);
      await refreshData();
      if (setActiveTab) setActiveTab('requests');
    } catch (err) {
      let msg = err.response?.data?.message || err.response?.data || 'Failed to submit leave request.';
      if (typeof msg === 'string' && (msg.toLowerCase().includes('overlap') || msg.toLowerCase().includes('existing'))) {
        msg = 'Your leave request could not be processed because you already have an existing leave application with overlapping dates.';
      }
      toast.error(typeof msg === 'string' ? msg : 'Error submitting request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelLeave = (leaveId) => {
    setCancelConfirm({ open: true, leaveId, loading: false });
  };

  const confirmCancelLeave = async () => {
    const { leaveId } = cancelConfirm;
    setCancelConfirm(prev => ({ ...prev, loading: true }));
    try {
      await api.put(`/leave/cancel/${leaveId}`, {});
      toast.success('Leave cancelled successfully.');
      setCancelConfirm({ open: false, leaveId: null, loading: false });
      await refreshData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || 'Failed to cancel leave.';
      toast.error(typeof msg === 'string' ? msg : 'Error cancelling leave');
      setCancelConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateStr) => fmtDate(dateStr);

  const getFilteredRequests = () => {
    if (requestFilter === 'ALL') return leaveHistory;
    return leaveHistory.filter(req => req.status === requestFilter);
  };

  const filteredRequests = getFilteredRequests();

  const renderRequestsTable = () => (
    <div className="flex-1 mt-4">
      {/* ── Desktop View (Table) ── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 border-y border-slate-200 text-slate-400 text-[10px] uppercase tracking-widest font-bold">
            <tr>
              <th className="px-6 py-4">Leave Details</th>
              <th className="px-6 py-4">Duration</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loadingHistory ? (
              <tr><td colSpan={4} className="py-12 text-center text-slate-500 font-medium">Loading history...</td></tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-16 text-center">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-dashed border-slate-200">
                    <FiFileText className="text-xl text-slate-400" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">No Leave Requests Found</h3>
                  <p className="text-slate-500 text-xs">No leave applications match the selected filter.</p>
                </td>
              </tr>
            ) : (
              filteredRequests.map((leave) => {
                const st = STATUS_UI[leave.status] || STATUS_UI.PENDING;
                const Icon = st.icon;
                
                const paid = leave.paidDays || 0;
                const unpaid = leave.unpaidDays || 0;
                
                return (
                  <tr key={leave.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900">{leave.leaveType}</span>
                        {leave.isHalfDay && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Half Day ({leave.halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})
                          </span>
                        )}
                        {leave.addedByManager && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            Added by Manager
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-medium text-slate-500 mt-1">
                        {formatDate(leave.startDate)} {leave.startDate !== leave.endDate && <><span className="mx-1 text-slate-300">→</span> {formatDate(leave.endDate)}</>}
                      </div>
                      {leave.reason && (
                        <div className="text-[11px] text-slate-400 italic mt-1.5 line-clamp-1 border-l-2 border-slate-200 pl-2">
                          {leave.reason}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline gap-1">
                          <span className="font-black text-slate-700 text-base">{leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : '—')}</span>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">{leave.isHalfDay ? 'day' : 'days'}</span>
                        </div>
                        <div className="flex gap-1.5 text-[10px] font-bold">
                          {paid > 0 && <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{paid} PAID</span>}
                          {unpaid > 0 && <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{unpaid} LOP</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded w-max text-[10px] font-bold uppercase tracking-wider border ${st.bg} ${st.color} ${st.border}`}>
                          <Icon size={12} /> {leave.status}
                        </span>
                        {leave.addedByManager ? (
                          <span className="text-[10px] text-gray-500 font-medium">
                            Recorded by {leave.approvedByUsername || 'Manager'}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 font-medium">Applied {formatDate(leave.appliedDate)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      {leave.status === 'PENDING' && canCancelLeave(leave.startDate) ? (
                        <button 
                          onClick={() => handleCancelLeave(leave.id)}
                          className="text-xs font-bold text-slate-500 hover:text-red-600 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 px-3 py-1.5 rounded-lg transition-all shadow-sm"
                        >
                          Cancel
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile View (Cards with exact same data and structure) ── */}
      <div className="block md:hidden divide-y divide-slate-100">
        {loadingHistory ? (
          <div className="py-10 text-center text-slate-500 font-medium text-xs">Loading history...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="py-12 text-center px-4">
            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-dashed border-slate-200">
              <FiFileText className="text-xl text-slate-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-1">No Leave Requests Found</h3>
            <p className="text-slate-500 text-xs">No leave applications match the selected filter.</p>
          </div>
        ) : (
          filteredRequests.map((leave) => {
            const st = STATUS_UI[leave.status] || STATUS_UI.PENDING;
            const Icon = st.icon;
            const paid = leave.paidDays || 0;
            const unpaid = leave.unpaidDays || 0;

            return (
              <div key={leave.id} className="p-4 space-y-2.5 hover:bg-slate-50/50 transition-colors">
                {/* Top row: Type + Badges & Status Pill */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-slate-900 text-sm">{leave.leaveType}</span>
                    {leave.isHalfDay && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        Half Day ({leave.halfDaySession === 'SECOND_HALF' ? '2nd' : '1st'})
                      </span>
                    )}
                    {leave.addedByManager && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                        By Manager
                      </span>
                    )}
                  </div>

                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0 ${st.bg} ${st.color} ${st.border}`}>
                    <Icon size={11} /> {leave.status}
                  </span>
                </div>

                {/* Dates & Duration row */}
                <div className="flex items-center justify-between text-xs text-slate-600 gap-2">
                  <div className="font-medium text-slate-700">
                    {formatDate(leave.startDate)} {leave.startDate !== leave.endDate && <><span className="mx-1 text-slate-300">→</span> {formatDate(leave.endDate)}</>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="font-bold text-slate-800 text-xs">
                      {leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : '—')} {leave.isHalfDay ? 'day' : 'days'}
                    </span>
                    {paid > 0 && <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.2 rounded text-[10px] font-bold">{paid} PAID</span>}
                    {unpaid > 0 && <span className="text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.2 rounded text-[10px] font-bold">{unpaid} LOP</span>}
                  </div>
                </div>

                {/* Reason row if present */}
                {leave.reason && (
                  <p className="text-[11px] text-slate-500 italic border-l-2 border-slate-200 pl-2 line-clamp-2">
                    {leave.reason}
                  </p>
                )}

                {/* Footer row: Meta + Cancel action */}
                <div className="flex items-center justify-between pt-1 text-[11px] text-slate-400">
                  <span className="truncate max-w-[200px]">
                    {leave.addedByManager 
                      ? `Recorded by ${leave.approvedByUsername || 'Manager'}` 
                      : `Applied ${formatDate(leave.appliedDate)}`}
                  </span>

                  {leave.status === 'PENDING' && canCancelLeave(leave.startDate) && (
                    <button 
                      onClick={() => handleCancelLeave(leave.id)}
                      className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1 rounded-lg transition-all shrink-0 cursor-pointer"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  return (
    <div className="animate-fade-in pb-12 space-y-6 relative">
      
      <ConfirmModal
        isOpen={cancelConfirm.open}
        onClose={() => setCancelConfirm({ open: false, leaveId: null, loading: false })}
        onConfirm={confirmCancelLeave}
        loading={cancelConfirm.loading}
        title="Cancel Leave Request?"
        message="Are you sure you want to cancel this leave request? This action cannot be undone."
        confirmText="Yes, Cancel"
        cancelText="Keep"
        variant="warning"
        maxWidth="max-w-[420px]"
      />

      <ConfirmModal
        isOpen={pendingSubmit.open}
        onClose={() => { setPendingSubmit({ open: false, warnings: [], unpaidDays: 0 }); setLopAccepted(false); }}
        onConfirm={executeSubmit}
        loading={submitting}
        confirmDisabled={pendingSubmit.unpaidDays > 0 && !lopAccepted}
        title={pendingSubmit.unpaidDays > 0 ? "Loss of Pay (LOP) Confirmation" : "Leave Request Warning"}
        variant={pendingSubmit.unpaidDays > 0 ? "danger" : "warning"}
        confirmText={pendingSubmit.unpaidDays > 0 ? "Accept LOP & Submit" : "Proceed Anyway"}
        maxWidth="max-w-[620px]"
        message={
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-medium">Please review the following before submitting:</p>
            
            {pendingSubmit.warnings.map((w, idx) => (
              <div key={idx} className={`p-4 rounded-xl border text-sm font-medium leading-relaxed ${w.includes('Loss of Pay') ? 'bg-red-50 text-red-800 border-red-200' : 'bg-amber-50 text-amber-900 border-amber-200'}`}>
                {w}
              </div>
            ))}

            {pendingSubmit.unpaidDays > 0 && (
              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="pt-0.5">
                    <input 
                      type="checkbox" 
                      checked={lopAccepted}
                      onChange={(e) => setLopAccepted(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-600 cursor-pointer"
                    />
                  </div>
                  <div className="text-sm text-slate-700 leading-relaxed">
                    <span className="font-bold block text-slate-900 mb-0.5">I understand and accept</span>
                    I acknowledge that this leave request will result in <strong>{pendingSubmit.unpaidDays} day(s)</strong> of Loss of Pay, which will be deducted from my upcoming salary.
                  </div>
                </label>
              </div>
            )}
          </div>
        }
        cancelText="Cancel"
      />

      {/* TAB 1: LEAVE BALANCE DASHBOARD */}
      {activeTab === 'balance' && (
        <div className="space-y-6">

          {/* Section 2: LEAVE BALANCE SUMMARY (6 Cards Grid) */}
          {loadingBalance ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3">
                {[1, 2, 3, 4, 5].map((i) => <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 animate-pulse h-28" />)}
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 animate-pulse h-24" />
            </div>
          ) : (
            <div className="space-y-4">

              {/* ── TOP: 5 KEY METRICS ────────────────────────────────────────── */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3">
                
                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">Usable This Month</span>
                  <span className={`text-2xl sm:text-3xl font-black leading-none ${ (leaveBalance.totalPaidLeavesAvailableMonth ?? 0) === 0 ? 'text-amber-500' : 'text-blue-600'}`}>{leaveBalance.totalPaidLeavesAvailableMonth ?? 0}</span>
                  <span className="text-[11px] sm:text-xs text-slate-400 mt-0.5">max {leaveBalance.monthlyPaidLeaveLimit ?? 3} days cap</span>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">Carry Forward</span>
                  <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">
                    {computedCarryForward}
                  </span>
                  <span className="text-[11px] sm:text-xs text-slate-400 mt-0.5">accumulated unused</span>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col gap-0.5 sm:gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">FY Total Balance</span>
                  <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">{leaveBalance.totalPaidLeavesAvailableYear ?? leaveBalance.currentAvailableBalance ?? 0}</span>
                  <span className="text-[11px] sm:text-xs text-slate-400 mt-0.5">of {(leaveBalance.annualLimit ?? TOTAL_ANNUAL_DAYS) + (leaveBalance.carriedForward ?? leaveBalance.totalCarryForward ?? 0) + (leaveBalance.earnedLeave ?? 0)} total days</span>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col gap-0.5 sm:gap-1 shadow-xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
                    Earned Leaves
                  </span>
                  <span className="text-2xl sm:text-3xl font-black text-slate-800 leading-none my-0.5 sm:my-1">{leaveBalance.earnedLeave ?? 0}</span>
                  <span className="text-[11px] sm:text-xs text-slate-400 mt-0.5">This Year</span>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-col justify-between col-span-2 md:col-span-1 shadow-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
                      Loss of Pay (LOP)
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 my-1">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">This Month</span>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className={`text-xl sm:text-2xl font-black leading-none ${ (leaveBalance.lopLeavesMonth ?? 0) > 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                          {leaveBalance.lopLeavesMonth ?? 0}
                        </span>
                        <span className="text-[10px] sm:text-xs font-semibold text-slate-500">
                          day{(leaveBalance.lopLeavesMonth ?? 0) === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col border-l border-slate-100 pl-2">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">This FY</span>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className={`text-xl sm:text-2xl font-black leading-none ${ (leaveBalance.lopLeavesYear ?? 0) > 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                          {leaveBalance.lopLeavesYear ?? 0}
                        </span>
                        <span className="text-[10px] sm:text-xs font-semibold text-slate-500">
                          day{(leaveBalance.lopLeavesYear ?? 0) === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* ── MORE DETAILS REDIRECT (SEPARATE PAGE ONLY) ── */}
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => navigate('/leave-breakdown', { state: { selectedYear } })}
                  className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 border border-blue-200 hover:border-blue-300 px-3.5 py-1.5 rounded-lg transition-all cursor-pointer shadow-2xs group"
                >
                  <span>More Details</span>
                  <FiArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          )}

          {/* LEAVE REQUESTS SECTION */}
          <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mt-4 sm:mt-6">
            <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 flex items-center gap-2"><FiFileText className="text-blue-600"/> Leave Requests</h3>
                <p className="text-[11px] sm:text-xs text-slate-500 font-medium">Filter and view your past and pending leave applications</p>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center p-1 bg-slate-100 rounded-xl text-xs font-bold border border-slate-200/60 overflow-x-auto max-w-full w-full sm:w-auto">
                <button
                  onClick={() => setRequestFilter('ALL')}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 ${requestFilter === 'ALL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  All ({leaveHistory.length})
                </button>
                <button
                  onClick={() => setRequestFilter('PENDING')}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 ${requestFilter === 'PENDING' ? 'bg-amber-50 text-amber-700 border border-amber-200 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Pending ({leaveHistory.filter(r => r.status === 'PENDING').length})
                </button>
                <button
                  onClick={() => setRequestFilter('APPROVED')}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 ${requestFilter === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Approved ({leaveHistory.filter(r => r.status === 'APPROVED').length})
                </button>
                <button
                  onClick={() => setRequestFilter('REJECTED')}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 ${requestFilter === 'REJECTED' ? 'bg-rose-50 text-rose-700 border border-rose-200 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Rejected ({leaveHistory.filter(r => r.status === 'REJECTED').length})
                </button>
              </div>
            </div>
            
            {renderRequestsTable()}
          </div>
        </div>
      )}

      {/* TAB 2: APPLY LEAVE */}
      {activeTab === 'apply' && (
        <div className="space-y-4">
          <div>
            <button
              type="button"
              onClick={() => setActiveTab('balance')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors cursor-pointer group"
            >
              <FiArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back to Leave Summary</span>
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
                <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm sm:text-base font-bold text-slate-800 flex items-center gap-2"><FiSend className="text-blue-600"/> Apply for Leave</h3>
                </div>
              <form onSubmit={validateAndSubmit} className="p-4 sm:p-6 flex-1 flex flex-col justify-between space-y-4">
                {/* Row 1 — Leave Type / Start Date / Duration */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  {/* Leave Type */}
                  <div className="md:col-span-1">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Leave Type</label>
                    <select name="leaveType" value={form.leaveType} onChange={handleChange} required className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium">
                      {LEAVE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>

                  {/* Start Date — defaults to tomorrow */}
                  <div className="md:col-span-1">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={form.startDate}
                      onChange={handleChange}
                      required
                      min={getMinStartDate(form.leaveType)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    />
                    {form.leaveType === 'ANNUAL' && (
                      <p className="text-[11px] text-amber-600 font-semibold mt-1">Must be applied at least 1 day in advance.</p>
                    )}
                  </div>

                  {/* Duration (days) — with direct 0.5 / half-day support */}
                  <div className="md:col-span-1">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Duration <span className="text-blue-500 normal-case font-semibold">(days)</span>
                      </label>
                      {form.duration === 0.5 && (
                        <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.2 rounded">
                          Half-Day
                        </span>
                      )}
                    </div>

                    {/* Duration input with -, +, and calendar */}
                    <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 h-[42px]">
                      <button
                        type="button"
                        onClick={() => {
                          const cur = Number(form.duration || 1);
                          if (cur > 1) {
                            handleChange({ target: { name: 'duration', value: cur - 1 } });
                          } else if (cur === 1) {
                            handleChange({ target: { name: 'duration', value: 0.5 } });
                          }
                        }}
                        className="w-10 h-full flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-lg font-bold shrink-0 cursor-pointer"
                        title="Decrease duration"
                      >−</button>
                      <input
                        type="number"
                        name="duration"
                        value={form.duration}
                        onChange={handleChange}
                        min="0.5"
                        max="30"
                        step="0.5"
                        required
                        className="flex-1 text-center text-sm font-bold text-slate-800 bg-transparent border-0 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const cur = Number(form.duration || 1);
                          if (cur === 0.5) {
                            handleChange({ target: { name: 'duration', value: 1 } });
                          } else {
                            handleChange({ target: { name: 'duration', value: cur + 1 } });
                          }
                        }}
                        className="w-10 h-full flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-lg font-bold shrink-0 cursor-pointer"
                        title="Increase duration"
                      >+</button>
                      
                      <div className="relative w-10 h-full shrink-0 border-l border-slate-200">
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors pointer-events-none">
                          <FiCalendar size={15} />
                        </div>
                        <input
                          type="date"
                          ref={endDatePickerRef}
                          title="Pick end date from calendar"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          min={form.startDate || ''}
                          onChange={(e) => {
                            if (!e.target.value || !form.startDate) return;
                            const [sy, sm, sd] = form.startDate.split('-').map(Number);
                            const [ey, em, ed] = e.target.value.split('-').map(Number);
                            const start = new Date(sy, sm - 1, sd);
                            const end   = new Date(ey, em - 1, ed);
                            const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
                            if (diff >= 1) {
                              handleChange({ target: { name: 'duration', value: diff } });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Show 'Ends on' only for multi-day leaves (> 1 day) */}
                    {form.duration > 1 && form.endDate && (
                      <p className="text-[11px] text-slate-400 font-semibold mt-1">
                        Ends on <span className="text-slate-700 font-bold">{fmtDate(form.endDate)}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Session Selector — simple small dropdown when duration is 0.5 */}
                {(Number(form.duration) === 0.5 || form.isHalfDay) && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider shrink-0">
                      Half-Day Session <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="halfDaySession"
                      value={form.halfDaySession || 'FIRST_HALF'}
                      onChange={handleChange}
                      className="w-full sm:w-auto min-w-[200px] px-3 py-2 sm:py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    >
                      <option value="FIRST_HALF">First Half (Morning)</option>
                      <option value="SECOND_HALF">Second Half (Afternoon)</option>
                    </select>
                  </div>
                )}

                {/* Overlapping Leaves Alert Banner */}
                {previewError && (
                  <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-xs text-rose-800">
                    <FiAlertCircle className="text-rose-600 mt-0.5 shrink-0" size={17} />
                    <div className="flex-1">
                      <p className="font-bold text-rose-900 text-sm">
                        {previewError.toLowerCase().includes('overlap') ? 'Leaves are overlapping' : 'Leave Dates Notice'}
                      </p>
                      <p className="text-rose-700 mt-0.5 leading-relaxed font-medium">
                        {previewError}
                      </p>
                    </div>
                  </div>
                )}

                {/* Reason — mandatory */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Reason <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <textarea
                    name="reason"
                    value={form.reason}
                    onChange={(e) => { handleChange(e); setReasonTouched(true); }}
                    onBlur={() => setReasonTouched(true)}
                    rows="3"
                    required
                    placeholder="Provide a reason for your leave request…"
                    className={`w-full px-3 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none transition-colors ${
                      (reasonTouched && !form.reason.trim()) ? 'border-red-300' : 'border-slate-300'
                    }`}
                  />
                  {(reasonTouched && !form.reason.trim()) && (
                    <p className="text-[11px] text-red-500 font-semibold mt-1">Reason is required before submitting.</p>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting || (previewError && (previewError.toLowerCase().includes('overlap') || previewError.toLowerCase().includes('existing')))}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {submitting 
                      ? 'Validating Request...' 
                      : (previewError && (previewError.toLowerCase().includes('overlap') || previewError.toLowerCase().includes('existing')))
                        ? 'Leaves are Overlapping'
                        : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4">

            {/* ── Leave Preview ───────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Leave Preview</h4>

              {/* Balance pills */}
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-gray-200 rounded-xl p-2 text-center flex flex-col justify-center">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Avail (Yr)</p>
                  <p className="text-base font-black text-blue-600">{leaveBalance.totalPaidLeavesAvailableYear ?? leaveBalance.currentAvailableBalance ?? 0}<span className="text-[10px] font-normal text-gray-400 ml-0.5">d</span></p>
                </div>
                <div className="border border-gray-200 rounded-xl p-2 text-center flex flex-col justify-center">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Left (Mo)</p>
                  <p className="text-base font-black text-blue-600">{leaveBalance.totalPaidLeavesAvailableMonth ?? 0}<span className="text-[10px] font-normal text-gray-400 ml-0.5">d</span></p>
                </div>
                <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-2 text-center flex flex-col justify-center">
                  <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider mb-0.5">Carry Fwd</p>
                  <p className="text-base font-black text-emerald-700">{computedCarryForward}<span className="text-[10px] font-normal text-emerald-600/70 ml-0.5">d</span></p>
                </div>
              </div>

              {/* Live preview result */}
              {previewLoading ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                  <FiClock className="animate-spin" size={13} /> Calculating...
                </div>
              ) : previewError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-xs text-rose-800">
                  <div className="flex items-start gap-2.5">
                    <FiAlertCircle className="text-rose-600 mt-0.5 shrink-0" size={16} />
                    <div className="flex-1">
                      <p className="font-bold text-rose-900">
                        {previewError.toLowerCase().includes('overlap') ? 'Leaves are overlapping' : 'Leave Preview Notice'}
                      </p>
                      <p className="text-[11px] text-rose-700 mt-1 font-medium leading-relaxed">
                        {previewError}
                      </p>
                    </div>
                  </div>
                </div>
              ) : preview && form.startDate && form.endDate ? (
                <div className={`rounded-xl border p-3 ${
                  preview.unpaidDays > 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className="text-xs font-bold mb-2" style={{ color: preview.unpaidDays > 0 ? '#b91c1c' : '#374151' }}>
                    {preview.unpaidDays > 0 ? '⚠ LOP will be incurred' : '✓ Fully paid leave'}
                  </p>
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-gray-400">Total days</span>
                      <p className="font-bold text-gray-900">{preview.daysCount}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Paid</span>
                      <p className="font-bold text-gray-900">{preview.paidDays}</p>
                    </div>
                    {preview.unpaidDays > 0 && (
                      <div>
                        <span className="text-gray-400">LOP</span>
                        <p className="font-bold text-red-600">{preview.unpaidDays}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* ── Saturday Working Summary ─────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Saturday Working</h4>
                <span className="text-[10px] font-semibold text-slate-400">
                  {MONTH_NAMES[(selectedSaturdayMonth - 1)]} {selectedYear}
                </span>
              </div>

              {loadingSaturday ? (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
                  <FiClock className="animate-spin" size={12} /> Loading...
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Worked</p>
                    <p className="text-xl font-black text-emerald-600">{saturdayData.saturdaysWorked ?? 0}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Saturdays</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Pending</p>
                    <p className={`text-xl font-black ${ (saturdayData.pendingSaturdaysToWork ?? 0) > 0 ? 'text-amber-500' : 'text-slate-400'}`}>
                      {saturdayData.pendingSaturdaysToWork ?? 0}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">to work</p>
                  </div>
                </div>
              )}

              {(saturdayData.pendingSaturdaysToWork ?? 0) > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                  <FiAlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] font-semibold text-amber-800 leading-relaxed">
                    You have <strong>{saturdayData.pendingSaturdaysToWork}</strong> Saturday(s) pending this month.
                    Missing them may result in additional LOP deductions.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* TAB 3: MY LEAVE REQUESTS */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><FiFileText className="text-blue-600"/> My Leave Requests</h3>
              <p className="text-xs text-slate-500 font-medium">Filter and view your past and pending leave applications</p>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center p-1 bg-slate-100 rounded-xl text-xs font-bold border border-slate-200/60 overflow-x-auto max-w-full w-full sm:w-auto">
              <button
                onClick={() => setRequestFilter('ALL')}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 cursor-pointer ${requestFilter === 'ALL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                All ({leaveHistory.length})
              </button>
              <button
                onClick={() => setRequestFilter('PENDING')}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 cursor-pointer ${requestFilter === 'PENDING' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Pending ({leaveHistory.filter(r => r.status === 'PENDING').length})
              </button>
              <button
                onClick={() => setRequestFilter('APPROVED')}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 cursor-pointer ${requestFilter === 'APPROVED' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Approved ({leaveHistory.filter(r => r.status === 'APPROVED').length})
              </button>
              <button
                onClick={() => setRequestFilter('REJECTED')}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all shrink-0 cursor-pointer ${requestFilter === 'REJECTED' ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Rejected ({leaveHistory.filter(r => r.status === 'REJECTED').length})
              </button>
            </div>
          </div>
          
            {renderRequestsTable()}
          </div>

      )}

      {/* 
        TAB 4: ATTENDANCE (Temporarily disabled / hidden from UI as requested. Code preserved for future reuse)
        {activeTab === 'attendance' && (
          <div className="space-y-4">
            ...
          </div>
        )}
      */}
    </div>
  );
}